import { t } from "./i18n";
import { isValidNpi } from "./npi";
import { textField, selectField } from "./form-fields";

// Raw values captured on the professional-path Referral details screen. Single source
// of truth for the shape; pdf/submit consume this as a type-only import.
export interface ProfessionalDetails {
  referralSource: string; // resolved value; the typed text when "Other" is chosen
  mrn: string;
  npi: string; // as entered, trimmed; may be flagged-but-kept
  hpn: string;
  assessorName: string;
  assessorCredentials: string;
}

export interface ReferralCallbacks {
  onContinue: (details: ProfessionalDetails) => void;
  onBack: () => void;
}

const SOURCE_OPTIONS = [
  "unspecified",
  "physician",
  "hospital",
  "snf",
  "alf",
  "home_health",
  "family_self",
  "other",
] as const;

export function renderReferral(
  container: HTMLElement,
  callbacks: ReferralCallbacks,
  initial?: ProfessionalDetails
): void {
  while (container.firstChild) container.removeChild(container.firstChild);

  const wrapper = document.createElement("div");
  wrapper.className = "contact"; // reuse the contact screen's layout container

  const heading = document.createElement("h2");
  heading.textContent = t("ui.referral.title");
  wrapper.appendChild(heading);

  const intro = document.createElement("p");
  intro.className = "help-text";
  intro.textContent = t("ui.referral.intro");
  wrapper.appendChild(intro);

  const form = document.createElement("form");
  form.noValidate = true;

  const sourceSelect = selectField(
    form,
    "referralSource",
    t("ui.referral.referral_source_label"),
    SOURCE_OPTIONS.map((o) => ({
      value: o === "unspecified" ? "" : o,
      label: t(`ui.referral.referral_source.${o}`),
    }))
  );

  const otherInput = textField(form, "referralSourceOther", t("ui.referral.referral_source_other_label"));
  const otherGroup = otherInput.parentElement as HTMLElement;
  otherGroup.hidden = true;

  const mrnInput = textField(form, "mrn", t("ui.referral.mrn_label"));

  const npiInput = textField(form, "npi", t("ui.referral.npi_label"), { helpText: t("ui.referral.npi_help") });
  npiInput.setAttribute("inputmode", "numeric");
  const npiWarning = document.createElement("p");
  npiWarning.className = "npi-warning";
  npiWarning.setAttribute("role", "status");
  npiWarning.hidden = true;
  npiWarning.textContent = t("ui.referral.npi_warning");
  npiInput.parentElement!.appendChild(npiWarning);

  const hpnInput = textField(form, "hpn", t("ui.referral.hpn_label"));
  const assessorNameInput = textField(form, "assessorName", t("ui.referral.assessor_name_label"));
  const assessorCredsInput = textField(form, "assessorCredentials", t("ui.referral.assessor_credentials_label"));

  const actions = document.createElement("div");
  actions.className = "contact-actions";

  const continueBtn = document.createElement("button");
  continueBtn.type = "submit";
  continueBtn.className = "btn-primary";
  continueBtn.dataset.action = "continue";
  continueBtn.textContent = t("ui.referral.continue");

  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "btn-secondary";
  backBtn.dataset.action = "back";
  backBtn.textContent = t("ui.referral.back");
  backBtn.addEventListener("click", () => callbacks.onBack());

  actions.appendChild(continueBtn);
  actions.appendChild(backBtn);
  form.appendChild(actions);
  wrapper.appendChild(form);
  container.appendChild(wrapper);

  sourceSelect.addEventListener("change", () => {
    const isOther = sourceSelect.value === "other";
    otherGroup.hidden = !isOther;
    if (!isOther) otherInput.value = "";
  });

  npiInput.addEventListener("input", () => {
    const v = npiInput.value.trim();
    npiWarning.hidden = v.length === 0 || isValidNpi(v);
  });

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    callbacks.onContinue(collect());
  });

  if (initial) {
    mrnInput.value = initial.mrn;
    npiInput.value = initial.npi;
    hpnInput.value = initial.hpn;
    assessorNameInput.value = initial.assessorName;
    assessorCredsInput.value = initial.assessorCredentials;

    if (initial.referralSource) {
      const slug = SOURCE_OPTIONS.find(
        (o) => o !== "unspecified" && o !== "other" &&
          t(`ui.referral.referral_source.${o}`) === initial.referralSource
      );
      if (slug) {
        sourceSelect.value = slug;
      } else {
        sourceSelect.value = "other";
        otherInput.value = initial.referralSource;
        otherGroup.hidden = false;
      }
    }

    // Reflect the NPI warning state for a prefilled invalid value.
    const npiTrimmed = initial.npi.trim();
    npiWarning.hidden = npiTrimmed.length === 0 || isValidNpi(npiTrimmed);
  }

  function collect(): ProfessionalDetails {
    const sel = sourceSelect.value;
    let referralSource = "";
    if (sel === "other") referralSource = otherInput.value.trim();
    else if (sel) referralSource = t(`ui.referral.referral_source.${sel}`);
    return {
      referralSource,
      mrn: mrnInput.value.trim(),
      npi: npiInput.value.trim(),
      hpn: hpnInput.value.trim(),
      assessorName: assessorNameInput.value.trim(),
      assessorCredentials: assessorCredsInput.value.trim(),
    };
  }
}

// Pre-labeled display pairs for the PDF, non-empty fields only. Keeps the pdf layer
// free of i18n and of the raw field-name mapping.
export function professionalPdfPairs(details: ProfessionalDetails): { label: string; value: string }[] {
  const rows: { key: keyof ProfessionalDetails; label: string }[] = [
    { key: "referralSource", label: t("ui.referral.referral_source_label") },
    { key: "mrn", label: t("ui.referral.mrn_label") },
    { key: "npi", label: t("ui.referral.npi_label") },
    { key: "hpn", label: t("ui.referral.hpn_label") },
    { key: "assessorName", label: t("ui.referral.assessor_name_label") },
    { key: "assessorCredentials", label: t("ui.referral.assessor_credentials_label") },
  ];
  return rows
    .filter((r) => details[r.key].trim().length > 0)
    .map((r) => ({ label: r.label, value: details[r.key].trim() }));
}

